import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANDING } from "@/constants/testIds";
import { localizeCategory } from "@/i18n/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles`, { params: { lang: i18n.language } });
        if (!cancelled) setPosts(data || []);
      } catch (e) {
        console.error("Failed to load articles", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const featured = posts[0];
  const grid = posts.slice(1, 13);

  return (
    <section
      id="blog"
      data-testid={LANDING.blog}
      className="bg-white py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-16 md:mb-20">
          <div className="md:col-span-7 reveal">
            <span className="eyebrow">{t("blog.eyebrow")}</span>
            <h2
              data-testid={LANDING.blogTitle}
              className="font-display text-4xl md:text-6xl text-[#1a365d] leading-[1.05] tracking-tight mt-4"
            >
              {t("blog.title")}
            </h2>
          </div>
          <div className="md:col-span-5 reveal delay-1">
            <p className="font-body text-[#475569] text-base md:text-lg leading-relaxed">
              {t("blog.intro")}
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-center text-[#475569] py-10">{t("common.loading")}</div>
        )}

        {/* Featured */}
        {!loading && featured && (
          <Link
            to={`/articles/${featured.slug}`}
            data-testid={LANDING.blogCard(featured.slug)}
            className="group card-lift block reveal mb-16 md:mb-20"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="zoom-wrap aspect-[16/10] w-full bg-[#fafaf7]">
                  <img
                    src={featured.image_url}
                    alt={featured.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-5">
                <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#d4af37] font-body">
                  {t("blog.featured")} · {localizeCategory(featured.category, i18n.language)}
                </span>
                <h3 className="font-display text-3xl md:text-4xl text-[#1a365d] mt-4 leading-[1.1] group-hover:text-[#d4af37] transition-colors">
                  {featured.title}
                </h3>
                <p className="font-body text-[#475569] mt-5 leading-relaxed">
                  {featured.excerpt}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-[#1a365d] group-hover:text-[#d4af37] transition-colors text-[11px] tracking-[0.28em] uppercase font-medium">
                  {t("blog.readArticle")} <ArrowUpRight size={14} strokeWidth={1.8} />
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        {!loading && grid.length > 0 && (
          <div
            data-testid={LANDING.blogList}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-8"
          >
            {grid.map((post, idx) => (
              <Link
                key={post.slug}
                to={`/articles/${post.slug}`}
                data-testid={LANDING.blogCard(post.slug)}
                className={`group card-lift block reveal delay-${(idx % 4) + 1}`}
              >
                <div className="zoom-wrap aspect-[4/5] w-full bg-[#fafaf7] relative">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#1a365d] font-medium">
                    {localizeCategory(post.category, i18n.language)}
                  </span>
                </div>
                <div className="pt-5">
                  <span className="text-[11px] tracking-[0.28em] uppercase text-[#475569] font-body">
                    {post.date}
                  </span>
                  <h3 className="font-display text-lg md:text-xl text-[#1a365d] mt-2.5 leading-[1.22] group-hover:text-[#d4af37] transition-colors">
                    {post.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 md:mt-20 flex justify-center reveal">
          <Link to="/news-archive" className="btn-primary">
            {t("blog.archiveCta")}
            <ArrowUpRight size={14} strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </section>
  );
}
